import { EventEmitter, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

// import { environment } from '@sbd-account-portal/app/environments/environment';
import { ENVIRONMENT_INJECTION_TOKEN } from '@sbd-doug-frontend/shared/lib/injections/environment-injection-token';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  CIPXQuoteRequestIntakeFormBondFamily,
  CIPXQuoteRequestIntakeFormBondFamiliesResponse,
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormStateBondsResponse,
  CIPXQuoteRequestIntakeFormStateBond,
  CIPXQuoteRequestIntakeFormRequestQuoteResponse,
  CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration,
  CIPXQuoteRequestIntakeFormPricingCalculatorResponse,
  CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption,
  IntakeFormStep,
  CIPXQuoteRequestIntakeFormRequestQuoteResponseResponseData,
  CIPXQuoteRequestIntakeFormQuoteRequest,
  CIPXQuoteRequestIntakeFormPrincipalSelectionType,
  CIPXQuoteRequestIntakeFormCommunications,
  CIPXQuoteRequestIntakeFormCommunicationsContact,
} from './intake-form.types';
import { FormGroup, FormArray, AbstractControl, Validators, FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { forceValidation, getFormErrors } from '@sbd-doug-frontend/shared/lib/public-forms';
import {
  sectionHasQuestions,
  getQuestionsForSection,
  getCollectionSetsForSection,
  isCollectionQuestionSet,
  isQuestionVisible,
} from './intake-form.helpers';
import { LoggingService } from '@sbd-doug-frontend/shared/lib/shared-services/logging.service';
import { GenericType } from '@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types';
import { isKnownContactEmail, isKnownContactPhone, isServiceEmail } from '@sbd-doug-frontend/shared/lib/public-validators';
import {
  CIPXQuoteRequestIntakeFormPrincipalsResponse,
  CIPXQuoteRequestIntakeFormPrincipalRecord,
  CIPXQuoteRequestIntakeFormPrincipalDetailResponse,
  CIPXQuoteRequestIntakeFormPrincipalDetail,
  CIPXQuoteRequestIntakeFormCommunicationPreference,
  CIPXQuoteRequestIntakeFormPrincipalContact,
} from './intake-form.types';

@Injectable({ providedIn: 'root' })
export class CIPXQuoteRequestIntakeFormService {
  private _httpClient = inject(HttpClient);
  private _environment = inject(ENVIRONMENT_INJECTION_TOKEN);
  private _loggingService = inject(LoggingService);
  currentStep = signal<IntakeFormStep>(IntakeFormStep.PrincipalSelection);
  stepChanged = new EventEmitter<IntakeFormStep>();

  companyNumberOwnersChanged = new EventEmitter<number>();
  companyTypeIdChanged = new EventEmitter<number>();

  resetForm = new EventEmitter<void>();

  bondFamilies = signal<Array<CIPXQuoteRequestIntakeFormBondFamily>>([]);
  bondFamiliesLoading = signal<boolean>(false);

  stateBonds = signal<CIPXQuoteRequestIntakeFormStateBond[]>([]);
  stateBondsLoading = signal<boolean>(false);
  selectedStateBond = signal<CIPXQuoteRequestIntakeFormStateBond | null>(null);

  pricingCalculatorConfigurationLoading = signal<boolean>(false);
  pricingCalculatorConfiguration = signal<CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration | null>(null);

  penaltyOptionsLoading = signal<boolean>(false);
  penaltyOptions = signal<CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption[] | null>(null);

  selectedPenaltyOption = signal<CIPXQuoteRequestIntakeFormPricingCalculatorPenaltyOption | null>(null);

  completedQuoteRequest = signal<CIPXQuoteRequestIntakeFormQuoteRequest | null>(null);

  principals = signal<GenericType[]>([]);
  principalsLoading = signal<boolean>(false);
  selectedPrincipalDetail = signal<CIPXQuoteRequestIntakeFormPrincipalDetail | null>(null);
  principalDetailLoading = signal<boolean>(false);
  lastSelectedAccessRightId = signal<string | null>(null);
  private _accessRightIdToUserId = signal<Record<string, string>>({});

  // Stored communications from principal detail for later application in Notifications step
  communicationPreferences = signal<CIPXQuoteRequestIntakeFormCommunicationPreference[] | null>(null);
  principalContacts = signal<CIPXQuoteRequestIntakeFormPrincipalContact[] | null>(null);

  goToStep(step: IntakeFormStep) {
    this.currentStep.set(step);
    this.stepChanged.emit(step);
  }

  resetData() {
    this.bondFamilies.set([]);
    this.stateBonds.set([]);
    this.selectedStateBond.set(null);
    this.pricingCalculatorConfiguration.set(null);
    this.penaltyOptions.set(null);
    this.selectedPenaltyOption.set(null);
    this.completedQuoteRequest.set(null);
    this.principals.set([]);
    this.principalsLoading.set(false);
    this.selectedPrincipalDetail.set(null);
    this.principalDetailLoading.set(false);
    this.lastSelectedAccessRightId.set(null);
    this.communicationPreferences.set(null);
    this.principalContacts.set(null);
  }

  getPrincipals(options?: { forMyself?: boolean; forceRefresh?: boolean }): Observable<GenericType[]> {
    const cached = this.principals();
    const forMyself = options?.forMyself === true;
    const forceRefresh = options?.forceRefresh === true;
    if (!forMyself && !forceRefresh && cached && cached.length > 0) {
      return of(cached);
    }

    this.principalsLoading.set(true);
    const params = new HttpParams({ fromObject: forMyself ? { for_myself: '1' } : {} });
    return this._httpClient
      .get<CIPXQuoteRequestIntakeFormPrincipalsResponse>(
        `${this._environment.apiBaseUrl}/v4/quote/request/principals`,
        { params }
      )
      .pipe(
        map((response) => {
          const list: CIPXQuoteRequestIntakeFormPrincipalRecord[] = response?.data?.principals ?? [];
          const items: GenericType[] = list.map((p) => ({
            label: p.company_name || '',
            value: String(p.account_user_access_right_id ?? ''),
          }));
          // Build lookup map for access_right_id -> user_id
          const mapObj: Record<string, string> = {};
          list.forEach((p) => {
            const key = String(p.account_user_access_right_id ?? '');
            const val = this._normalizeUserId(p.user_id);
            if (key) {
              mapObj[key] = val;
            }
          });
          this._accessRightIdToUserId.set(mapObj);
          // Only cache the non-forMyself list globally
          if (!forMyself) {
            this.principals.set(items);
          }
          this.principalsLoading.set(false);
          return items;
        }),
        catchError((err) => {
          this.principals.set([]);
          this.principalsLoading.set(false);
          return throwError(() => new Error(err));
        })
      );
  }

  storeCommunicationData(
    preferences: CIPXQuoteRequestIntakeFormCommunicationPreference[] | null | undefined,
    principalContacts?: CIPXQuoteRequestIntakeFormPrincipalContact[] | null
  ): void {
    this.communicationPreferences.set(Array.isArray(preferences) ? preferences : null);
    this.principalContacts.set(Array.isArray(principalContacts) ? principalContacts : null);
  }

  clearStoredCommunicationData(): void {
    this.communicationPreferences.set(null);
    this.principalContacts.set(null);
  }

  getStoredCommunicationData(): {
    communicationPreferences: CIPXQuoteRequestIntakeFormCommunicationPreference[] | null;
    principalContacts: CIPXQuoteRequestIntakeFormPrincipalContact[] | null;
  } {
    return {
      communicationPreferences: this.communicationPreferences(),
      principalContacts: this.principalContacts(),
    };
  }

  /** Normalize user id for comparison (API may send number or string). */
  private _normalizeUserId(v: string | number | null | undefined): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'number' && !Number.isNaN(v)) return String(v);
    const s = String(v).trim();
    const n = Number(s);
    return !Number.isNaN(n) ? String(n) : s;
  }

  getUserIdForAccessRight(accessRightId: string | number | null | undefined): string | null {
    if (accessRightId === null || accessRightId === undefined) {
      return null;
    }
    const key = String(accessRightId);
    const mapObj = this._accessRightIdToUserId();
    return mapObj[key] ?? null;
  }

  findPrincipalByUserId(userId: string | number, options?: { useCache?: boolean }): Observable<CIPXQuoteRequestIntakeFormPrincipalRecord | null> {
    const id = this._normalizeUserId(userId);
    if (!id) {
      return of(null);
    }

    const fromMap = (): CIPXQuoteRequestIntakeFormPrincipalRecord | null => {
      const mapObj = this._accessRightIdToUserId();
      const accessRight = Object.keys(mapObj).find((k) => this._normalizeUserId(mapObj[k]) === id) ?? null;
      if (!accessRight) return null;
      return {
        account_user_access_right_id: Number(accessRight),
        user_id: id,
      } as CIPXQuoteRequestIntakeFormPrincipalRecord;
    };

    if (options?.useCache === true) {
      const cached = fromMap();
      if (cached) return of(cached);
    }

    return this.getPrincipals({ forceRefresh: true }).pipe(
      map(() => fromMap()),
      catchError(() => of(null))
    );
  }

  loadAndApplyPrincipalByAccessRightId(
    accessRightId: string | number,
    form: FormGroup<CIPXQuoteRequestIntakeForm>
  ): Observable<CIPXQuoteRequestIntakeFormPrincipalDetail> {
    return this.getPrincipalDetail(accessRightId).pipe(
      map((detail) => {
        this.applyPrincipalDetailToForm(detail, form);

        // Store communication data for Notifications step to consume later
        this.storeCommunicationData(
          detail.communication?.communication_preferences ?? null,
          detail.communication?.principal_contacts ?? null
        );

        return detail;
      })
    );
  }

  applyPrincipalDetailToForm(
    detail: CIPXQuoteRequestIntakeFormPrincipalDetail,
    form: FormGroup<CIPXQuoteRequestIntakeForm>
  ): void {
    // Top-level user_id for quote request submission (API may return number or string)
    const detailUserId = (detail as { user_id?: string | number | null }).user_id;
    const normalizedUserId = this._normalizeUserId(detailUserId);
    form.controls.user_id.setValue(normalizedUserId || null);

    // Principal basic fields
    const requestDetailsGroup = form.controls.requestDetails;
    requestDetailsGroup.controls.principal.patchValue({
      company_name: detail.company_name ?? '',
      company_dba: detail.company_dba ?? '',
      company_address_street_1: (detail as any).company_address_street_1 ?? '',
      company_address_street_2: (detail as any).company_address_street_2 ?? '',
      company_address_city: (detail as any).company_address_city ?? '',
      company_address_state_abbreviation: (detail as any).company_address_state_abbreviation ?? '',
      company_address_zip: (detail as any).company_address_zip ?? '',
      company_address_county: (detail as any).company_address_county ?? '',
    });

    // Toggle addCompanyAddress
    const hasCompanyAddress = Boolean(
      (detail as any).company_address_id ||
        (detail as any).company_address_street_1 ||
        (detail as any).company_address_city ||
        (detail as any).company_address_state_abbreviation ||
        (detail as any).company_address_zip
    );
    if (hasCompanyAddress) {
      requestDetailsGroup.controls.addCompanyAddress.setValue(true);
    }

    // Company type id
    const companyTypeId = Number(detail.company_type_id ?? 3);
    form.controls.bondLookup.controls.company_type_id.setValue(companyTypeId);
    this.companyTypeIdChanged.emit(companyTypeId);

    // Company years in business
    requestDetailsGroup.controls.company_years_in_business.setValue(
      Number((detail as any).company_years_in_business ?? 0)
    );

    // Owners
    const owners = Array.isArray(detail.owners) ? detail.owners : [];
    const ownersCount = owners.length > 0 ? owners.length : 1;
    this.companyNumberOwnersChanged.emit(ownersCount);

    // The owner record itself often comes back without an email even when the
    // person is a known user; their real address lives on the principal contact
    // row, so fall back to it (never surface service+ aliases either way).
    const principalContacts = detail.communication?.principal_contacts ?? [];
    const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
    const contactMatch = (first: string, last: string) =>
      principalContacts.find(
        (c) => norm(c.user_first_name) === norm(first) && norm(c.user_last_name) === norm(last)
      );
    const contactEmailFor = (first: string, last: string): string => {
      const email = String(contactMatch(first, last)?.user_email_address ?? '').trim();
      return isKnownContactEmail(email) ? email : '';
    };
    const contactPhoneFor = (first: string, last: string): string => {
      const phone = String(contactMatch(first, last)?.user_phone ?? '');
      return isKnownContactPhone(phone) ? phone : '';
    };

    setTimeout(() => {
      const ownersArray = requestDetailsGroup.controls.owners;
      for (let i = 0; i < ownersArray.length && i < owners.length; i++) {
        const src = owners[i];
        const dst = ownersArray.at(i);
        const ownEmail = isKnownContactEmail(src.email) ? (src.email ?? '') : '';
        const ownPhone = isKnownContactPhone(src.phone) ? (src.phone ?? '') : '';
        dst.patchValue({
          owner_first_name: src.first_name ?? '',
          owner_middle_name: src.middle_name ?? '',
          owner_last_name: src.last_name ?? '',
          owner_email: ownEmail || contactEmailFor(src.first_name ?? '', src.last_name ?? ''),
          owner_phone: ownPhone || contactPhoneFor(src.first_name ?? '', src.last_name ?? ''),
          owner_ssn: src.ssn ?? '',
          owner_marital_status: src.marital_status_id ?? null,
          owner_dob: src.dob ?? '',
          owner_years_experience: src.years_experience ? Number(src.years_experience) : null,
          owner_percent_owned: src.percent_owned ? Number(src.percent_owned) : null,
          owner_title: '',
          owner_address_street_1: src.home_address_street_1 ?? '',
          owner_address_street_2: src.home_address_street_2 ?? '',
          owner_address_city: src.home_address_city ?? '',
          owner_address_state_abbreviation: src.home_address_state_abbreviation ?? '',
          owner_address_zip: src.home_address_zip ?? '',
        });

        const emailCtrl = dst.get('owner_email');
        if (emailCtrl) {
          const email = String(emailCtrl.getRawValue() ?? '').trim();
          if (isServiceEmail(email)) emailCtrl.setValue('', { emitEvent: false });
          else if (isKnownContactEmail(email)) emailCtrl.disable({ emitEvent: false });
        }
        const phoneCtrl = dst.get('owner_phone');
        if (phoneCtrl && isKnownContactPhone(phoneCtrl.getRawValue())) {
          phoneCtrl.disable({ emitEvent: false });
        }
      }
    }, 0);
  }

  getPrincipalDetail(accessRightId: string | number): Observable<CIPXQuoteRequestIntakeFormPrincipalDetail> {
    this.principalDetailLoading.set(true);
    this.lastSelectedAccessRightId.set(String(accessRightId));
    return this._httpClient
      .get<CIPXQuoteRequestIntakeFormPrincipalDetailResponse>(
        `${this._environment.apiBaseUrl}/v4/quote/request/principal/${accessRightId}`
      )
      .pipe(
        map((response) => {
          const detail = response?.data?.principal as CIPXQuoteRequestIntakeFormPrincipalDetail;
          this.selectedPrincipalDetail.set(detail);
          this.principalDetailLoading.set(false);
          return detail;
        }),
        catchError((err) => {
          this.selectedPrincipalDetail.set(null);
          this.principalDetailLoading.set(false);
          return throwError(() => new Error(err));
        })
      );
  }

  getBondFamiliesByStateAbbreviation(stateAbbreviation: string): Observable<CIPXQuoteRequestIntakeFormBondFamily[]> {
    this.bondFamiliesLoading.set(true);

    return this._httpClient
      .get<CIPXQuoteRequestIntakeFormBondFamiliesResponse>(`${this._environment.apiBaseUrl}/v4/quote/bond-families`, {
        params: {
          state_abbreviation: stateAbbreviation,
        },
      })
      .pipe(
        map((response: CIPXQuoteRequestIntakeFormBondFamiliesResponse) => {
          this.bondFamilies.set(response.data.bond_families.bond_families);
          this.bondFamiliesLoading.set(false);
          return response.data.bond_families.bond_families;
        }),
        catchError((err) => {
          this.bondFamilies.set([]);
          this.bondFamiliesLoading.set(false);
          return throwError(() => new Error(err));
        })
      );
  }

  getStateBondsByStateAbbreviationAndSlug(
    stateAbbreviation: string,
    slug: string
  ): Observable<CIPXQuoteRequestIntakeFormStateBond[]> {
    this.stateBondsLoading.set(true);

    return this._httpClient
      .get<CIPXQuoteRequestIntakeFormStateBondsResponse>(`${this._environment.apiBaseUrl}/v4/quote/state-bonds`, {
        params: {
          state_abbreviation: stateAbbreviation,
          slug: slug,
        },
      })
      .pipe(
        map((response) => {
          this._loggingService.log('sbs are', response);
          this.stateBonds.set(response.data.state_bonds.state_bonds);
          this.stateBondsLoading.set(false);
          return response.data.state_bonds.state_bonds;
        }),
        catchError((err) => {
          this.stateBonds.set([]);
          this.stateBondsLoading.set(false);
          return throwError(() => new Error(err));
        })
      );
  }

  getPricingCalculatorConfiguration(
    stateBond: CIPXQuoteRequestIntakeFormStateBond
  ): Observable<CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration> {
    this.pricingCalculatorConfigurationLoading.set(true);
    return this._httpClient
      .get<CIPXQuoteRequestIntakeFormPricingCalculatorResponse>(
        `${this._environment.apiBaseUrl}/v4/quote/state-bond/${stateBond.state_bond_id}/pricing-configuration`
      )
      .pipe(
        map((response) => {
          this._loggingService.log('pricing config ', response);
          this.penaltyOptions.set(response.data.pricing_configuration.penalty.options ?? null);
          this.pricingCalculatorConfiguration.set(response.data.pricing_configuration);
          this.pricingCalculatorConfigurationLoading.set(false);
          return response.data.pricing_configuration;
        }),
        catchError((err) => {
          this.penaltyOptions.set(null);
          this.pricingCalculatorConfiguration.set(null);
          this.pricingCalculatorConfigurationLoading.set(false);
          return throwError(() => new Error(err));
        })
      );
  }

  calculatePenaltyOptions(
    quoteRequestForm: FormGroup<CIPXQuoteRequestIntakeForm>,
    stateBond: CIPXQuoteRequestIntakeFormStateBond
  ): Observable<CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration> {
    this.penaltyOptionsLoading.set(true);

    const p = {
      questions: {
        bond_amount: quoteRequestForm.value.bondLookup?.bond_amount
          ? parseInt(quoteRequestForm.value.bondLookup.bond_amount.toString())
          : 0,
      },
      additional_questions: quoteRequestForm.value.additional_questions,
    };

    return this._httpClient
      .post<CIPXQuoteRequestIntakeFormPricingCalculatorResponse>(
        `${this._environment.apiBaseUrl}/v4/quote/state-bond/${stateBond.state_bond_id}/calculate`,
        p
      )
      .pipe(
        map((response) => {
          this.penaltyOptionsLoading.set(false);
          this.penaltyOptions.set(response.data.pricing_configuration.penalty.options ?? null);
          this.pricingCalculatorConfiguration.set(response.data.pricing_configuration);
          // this.pricingCalculatorConfigurationLoading.set(false);
          return response.data.pricing_configuration;
        }),
        catchError((err) => {
          this.penaltyOptions.set(null);
          this.penaltyOptionsLoading.set(false);
          // this.pricingCalculatorConfiguration.set(null);
          // this.pricingCalculatorConfigurationLoading.set(false);
          return throwError(() => new Error(err));
        })
      );
  }

  clearBondFamilies() {
    this.bondFamilies.set([]);
  }

  clearStateBonds() {
    this.stateBonds.set([]);
  }

  requestQuote(
    quoteRequestForm: FormGroup<CIPXQuoteRequestIntakeForm>
  ): Observable<CIPXQuoteRequestIntakeFormRequestQuoteResponse> {
    // Flatten the form structure for the API request (includes type conversion)
    const formData = this.flattenFormData(quoteRequestForm.getRawValue(), quoteRequestForm);

    const datePipe = new DatePipe('en-US');

    const requestedCoverageBeginDateRaw: string | Date =
      quoteRequestForm.value.requested_coverage_begin_date ?? new Date();
    const requestedCoverageBeginDateFormatted = datePipe.transform(requestedCoverageBeginDateRaw, 'yyyy-MM-dd');

    formData.requested_coverage_begin_date = requestedCoverageBeginDateFormatted;

    // Log the type-converted form data
    this._loggingService.log('Form Data with Type Conversion:', formData);
    return this._httpClient
      .post<CIPXQuoteRequestIntakeFormRequestQuoteResponse>(
        `${this._environment.apiBaseUrl}/v4/quote/request`,
        formData
      )
      .pipe(
        map((response) => {
          this.completedQuoteRequest.set(response.data.quote_request.quote_request);
          return response;
        })
      );
  }

  /**
   * Validates the current step including both step-specific fields and relevant additional questions
   */
  validateCurrentStep(
    step: IntakeFormStep,
    quoteRequestForm: FormGroup<CIPXQuoteRequestIntakeForm>,
    stateBond: CIPXQuoteRequestIntakeFormStateBond | null
  ): boolean {
    let isValid = true;
    const stepName = IntakeFormStep[step];

    this._loggingService.log(`🔍 Validating step: ${stepName}`);

    // 1. Validate the step-specific FormGroup
    const stepFormGroup = this.getFormGroupForStep(step, quoteRequestForm);
    if (stepFormGroup) {
      // Use a non-intrusive validation approach to prevent triggering valueChanges
      stepFormGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });

      // For RequestDetails step, also validate nested FormArrays
      if (step === IntakeFormStep.RequestDetails) {
        this.validateFormArraysInGroup(stepFormGroup);
      }

      isValid = stepFormGroup.valid;

      // Additional validation: PrincipalSelection requires a selected principal and loaded details when 'existing'
      if (isValid && step === IntakeFormStep.PrincipalSelection) {
        const selectionType = (stepFormGroup.get('principalSelectionType')?.value ??
          null) as CIPXQuoteRequestIntakeFormPrincipalSelectionType | null;
        if (selectionType === 'existing') {
          const existingVal = stepFormGroup.get('existingPrincipal')?.value;
          const hasSelected = existingVal && typeof existingVal === 'object' && !!existingVal.value;
          const detailLoaded = this.selectedPrincipalDetail() !== null && this.principalDetailLoading() === false;
          if (!hasSelected || !detailLoaded) {
            isValid = false;
          }
        }
      }

      // Additional validation: RequestDetails requires effective date
      if (isValid && step === IntakeFormStep.RequestDetails) {
        const effCtrl = quoteRequestForm.controls.requested_coverage_begin_date;
        effCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        if (effCtrl.invalid) {
          this._loggingService.log('❌ Missing required requested_coverage_begin_date');
          isValid = false;
        }
      }

      // Additional validation: Communications requires at least one email enabled with a value
      // if (isValid && step === IntakeFormStep.Notifications) {
      //   const comms = quoteRequestForm.controls.communications as unknown as FormGroup;
      //   const contactsArray = comms.get('contacts') as FormArray | null;
      //   let hasAnyEmail = false;
      //   if (contactsArray && contactsArray.length) {
      //     for (const ctrl of contactsArray.controls) {
      //       const gg = ctrl as FormGroup;
      //       const ann = gg.get('communication_config.communication_group.quote_announcements') as FormGroup;
      //       const emailEnabled = !!ann.get('email.enabled')?.value;
      //       const emailValue = (ann.get('email.email')?.value ?? '').toString().trim();
      //       if (emailEnabled && emailValue) {
      //         hasAnyEmail = true;
      //         break;
      //       }
      //     }
      //   }
      //   if (!hasAnyEmail) {
      //     this._loggingService.log('❌ Communications: At least one email must be enabled with a value');
      //     isValid = false;
      //   }
      // }

      if (!isValid) {
        const formErrors = getFormErrors(stepFormGroup);
        this._loggingService.log(`❌ Step "${stepName}" form validation failed:`, formErrors);

        // Log individual field errors for easier debugging
        this.logFormGroupErrors(stepFormGroup, '  ');
      } else {
        this._loggingService.log(`✅ Step "${stepName}" form validation passed`);
      }
    }

    // 2. Validate additional questions for this step's sections (align with UI rendering)
    const sectionNames = this.getSectionNamesForStep(step);
    if (stateBond) {
      sectionNames.forEach((sectionName) => {
        if (!sectionHasQuestions(sectionName, stateBond)) {
          return;
        }

        const questionsValid = this.validateQuestionsForSection(sectionName, stateBond, quoteRequestForm);

        if (!questionsValid) {
          this._loggingService.log(`❌ Additional questions validation failed for section "${sectionName}"`);
        } else {
          this._loggingService.log(`✅ Additional questions validation passed for section "${sectionName}"`);
        }

        isValid = isValid && questionsValid;
      });
    }

    this._loggingService.log(
      `🎯 Overall validation result for step "${stepName}": ${isValid ? '✅ PASSED' : '❌ FAILED'}`
    );
    return isValid;
  }

  /**
   * Get detailed validation information for debugging
   */
  getValidationDetails(
    step: IntakeFormStep,
    quoteRequestForm: FormGroup<CIPXQuoteRequestIntakeForm>,
    stateBond: CIPXQuoteRequestIntakeFormStateBond | null
  ): { isValid: boolean; errors: any; fieldErrors: { [key: string]: any } } {
    const stepFormGroup = this.getFormGroupForStep(step, quoteRequestForm);
    const result = {
      isValid: true,
      errors: {},
      fieldErrors: {} as { [key: string]: any },
    };

    if (stepFormGroup) {
      // Use a non-intrusive validation approach to prevent triggering valueChanges
      stepFormGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      result.isValid = stepFormGroup.valid;
      result.errors = getFormErrors(stepFormGroup);

      // Get individual field errors
      Object.keys(stepFormGroup.controls).forEach((controlName) => {
        const control = stepFormGroup.get(controlName);
        if (control && control.invalid) {
          result.fieldErrors[controlName] = control.errors;
        }
      });
    }

    return result;
  }

  private validateFormArraysInGroup(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((controlName) => {
      const control = formGroup.get(controlName);

      if (control && control instanceof FormArray) {
        this._loggingService.log(`🔄 Validating FormArray "${controlName}" with ${control.length} items`);

        // Validate each item in the FormArray
        control.controls.forEach((arrayControl: AbstractControl, index: number) => {
          if (arrayControl instanceof FormGroup) {
            arrayControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

            if (!arrayControl.valid) {
              this._loggingService.log(`  ❌ FormArray item [${index}] is invalid:`);
              this.logFormGroupErrors(arrayControl, '    ');
            } else {
              this._loggingService.log(`  ✅ FormArray item [${index}] is valid`);
            }
          }
        });

        // Update the FormArray itself
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      } else if (control && control instanceof FormGroup) {
        // Recursively validate nested FormGroups
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.validateFormArraysInGroup(control);
      }
    });
  }

  private logFormGroupErrors(formGroup: FormGroup, indent = ''): void {
    Object.keys(formGroup.controls).forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (control && control.invalid) {
        if (control instanceof FormGroup) {
          this._loggingService.log(`${indent}🔸 FormGroup "${controlName}" errors:`, control.errors);
          this.logFormGroupErrors(control, indent + '  ');
        } else if (control instanceof FormArray) {
          this._loggingService.log(`${indent}🔸 FormArray "${controlName}" errors:`, control.errors);
          control.controls.forEach((arrayControl: AbstractControl, index: number) => {
            if (arrayControl.invalid && arrayControl instanceof FormGroup) {
              this._loggingService.log(`${indent}  🔸 Array item [${index}] errors:`);
              this.logFormGroupErrors(arrayControl, indent + '    ');
            }
          });
        } else {
          this._loggingService.log(`${indent}🔸 Field "${controlName}" errors:`, control.errors);
        }
      }
    });
  }

  private getFormGroupForStep(
    step: IntakeFormStep,
    quoteRequestForm: FormGroup<CIPXQuoteRequestIntakeForm>
  ): FormGroup | null {
    switch (step) {
      case IntakeFormStep.PrincipalSelection:
        return quoteRequestForm.controls.principalSelection;
      case IntakeFormStep.BondLookup:
        return quoteRequestForm.controls.bondLookup;
      case IntakeFormStep.RequestDetails:
        return quoteRequestForm.controls.requestDetails;
      case IntakeFormStep.Notifications:
        return quoteRequestForm.controls.communications as unknown as FormGroup;
      // Add other steps as needed
      default:
        return null;
    }
  }

  private getSectionNamesForStep(step: IntakeFormStep): string[] {
    switch (step) {
      case IntakeFormStep.BondLookup:
        return ['state_bond_option_selection'];
      case IntakeFormStep.RequestDetails:
        return ['request_details'];
      case IntakeFormStep.AdditionalQuestions:
        return ['project_information', 'court', 'screening'];
      // Add other steps as needed
      default:
        return [];
    }
  }

  /**
   * Validates a section's flat questions and collection sets, skipping
   * questions that are currently hidden by their display/explainer conditions.
   */
  private validateQuestionsForSection(
    sectionName: string,
    stateBond: CIPXQuoteRequestIntakeFormStateBond,
    quoteRequestForm: FormGroup<CIPXQuoteRequestIntakeForm>
  ): boolean {
    const additionalQuestionsControl = quoteRequestForm.controls.additional_questions as FormGroup;
    if (!additionalQuestionsControl) {
      return true;
    }

    let isValid = true;

    const validateQuestion = (question: { question_code: string; question_text: string }, group: FormGroup): void => {
      const control = group.get(question.question_code);
      if (!control) {
        isValid = false;
        return;
      }

      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      if (control.invalid) {
        this._loggingService.log(
          `  🔸 Question "${question.question_code}" (${question.question_text}) is invalid:`,
          control.errors
        );
        isValid = false;
      }
    };

    getQuestionsForSection(sectionName, stateBond).forEach((question) => {
      if (isQuestionVisible(question, additionalQuestionsControl)) {
        validateQuestion(question, additionalQuestionsControl);
      }
    });

    getCollectionSetsForSection(sectionName, stateBond).forEach((questionSet) => {
      const items = additionalQuestionsControl.get(questionSet.question_set_code);
      if (!(items instanceof FormArray)) {
        isValid = false;
        return;
      }

      const collection = questionSet.question_set.collection ?? {};
      const minItems = Math.max(1, collection.min_items ?? 1);
      if (items.length < minItems || (collection.max_items !== undefined && items.length > collection.max_items)) {
        this._loggingService.log(
          `  🔸 Collection "${questionSet.question_set_code}" has ${items.length} items, expected ${minItems}-${collection.max_items ?? '∞'}`
        );
        isValid = false;
      }

      items.controls.forEach((itemGroup) => {
        if (!(itemGroup instanceof FormGroup)) {
          return;
        }
        questionSet.questions.forEach((question) => {
          if (isQuestionVisible(question, itemGroup)) {
            validateQuestion(question, itemGroup);
          }
        });
      });
    });

    return isValid;
  }

  private flattenFormData(formValue: any, originalForm?: FormGroup<CIPXQuoteRequestIntakeForm>): any {
    const bondLookupFlatted = {...formValue.bondLookup};
    if (bondLookupFlatted.stateBondSelection) {
      delete bondLookupFlatted.stateBondSelection;
    }
    const flattened = {
      // Principal selection fields
      ...formValue.principalSelection,
      // Bond lookup fields
      ...bondLookupFlatted,
      // Request details fields
      ...formValue.requestDetails,
      // Shared fields
      state_bond_id: formValue.state_bond_id,
      requested_coverage_begin_date: formValue.requested_coverage_begin_date,
      additional_questions: formValue.additional_questions,
      additional_comments: formValue.additional_comments,
      user_id: formValue.user_id,
    };

    // Convert numeric fields from strings to numbers using form control types
    const converted = this.convertNumericFields(flattened, originalForm);

    if (originalForm) {
      const communications = this.serializeContactsPayload(originalForm as any);
      if (communications) {
        (converted as any).communications = communications;
      }
    }

    return converted;
  }

  private convertNumericFields(data: any, originalForm?: FormGroup<CIPXQuoteRequestIntakeForm>): any {
    if (!originalForm) {
      // Fallback - if no form provided, return data as-is
      return data;
    }

    return this.convertBasedOnFormControls(data, originalForm);
  }

  private convertBasedOnFormControls(data: any, formGroup: FormGroup, dataPath = ''): any {
    const converted = { ...data };

    Object.keys(formGroup.controls).forEach((controlKey) => {
      const control = formGroup.get(controlKey);
      const dataValue = converted[controlKey];

      if (control instanceof FormGroup) {
        // Recursively handle nested form groups
        if (dataValue && typeof dataValue === 'object') {
          converted[controlKey] = this.convertBasedOnFormControls(dataValue, control, `${dataPath}.${controlKey}`);
        }
      } else if (control instanceof FormArray) {
        // Handle form arrays (like owners)
        if (Array.isArray(dataValue)) {
          converted[controlKey] = dataValue.map((item: any, index: number) => {
            const arrayControl = control.at(index);
            if (arrayControl instanceof FormGroup) {
              return this.convertBasedOnFormControls(item, arrayControl, `${dataPath}.${controlKey}[${index}]`);
            }
            return item;
          });
        }
      } else if (control) {
        // Handle individual form controls - check if the control expects a number
        if (this.shouldControlValueBeNumber(control, dataValue)) {
          converted[controlKey] = Number(dataValue);
        }
      }
    });

    // Handle additional questions separately since they're dynamic
    if (dataPath === '' && converted.additional_questions && typeof converted.additional_questions === 'object') {
      const selectedStateBond = this.selectedStateBond();
      if (selectedStateBond) {
        converted.additional_questions = this.convertAdditionalQuestionsNumericFields(
          converted.additional_questions,
          selectedStateBond
        );
      }
    }

    return converted;
  }

  private shouldControlValueBeNumber(control: AbstractControl, value: any): boolean {
    // Skip if value is null, undefined, empty string, or already a number
    if (value === null || value === undefined || value === '' || typeof value === 'number') {
      return false;
    }

    // Only convert if it's a string that represents a valid number
    if (typeof value !== 'string' || isNaN(Number(value))) {
      return false;
    }

    // Simple test: try to set the control to a number and see if it accepts it
    // This works because FormControl<number> will accept numbers but FormControl<string> might not behave the same way
    const originalValue = control.value;

    try {
      // Try setting to a number
      control.setValue(42, { emitEvent: false });
      const acceptedNumber = control.value === 42;

      // Try setting to a string
      control.setValue('test', { emitEvent: false });
      const acceptedString = control.value === 'test';

      // Restore original value
      control.setValue(originalValue, { emitEvent: false });

      // If it accepts both numbers and strings equally, we need to check validators
      // Controls typed as FormControl<number> often have min/max validators
      if (acceptedNumber && acceptedString) {
        // Check if control has numeric validators by testing with invalid numeric values
        control.setValue(-999999, { emitEvent: false });
        const hasMinError = control.errors?.['min'];
        control.setValue(999999, { emitEvent: false });
        const hasMaxError = control.errors?.['max'];
        control.setValue(originalValue, { emitEvent: false });

        return hasMinError || hasMaxError;
      }

      return acceptedNumber;
    } catch {
      // Restore original value on error
      control.setValue(originalValue, { emitEvent: false });
      return false;
    }
  }

  private convertAdditionalQuestionsNumericFields(
    additionalQuestions: any,
    stateBond: CIPXQuoteRequestIntakeFormStateBond
  ): any {
    if (!stateBond.additional_questions) {
      return additionalQuestions;
    }

    const converted = { ...additionalQuestions };
    const numericQuestionTypes = ['number', 'integer', 'decimal', 'float', 'int', 'currency'];

    const toNumber = (value: any): any =>
      value !== null && value !== undefined && value !== '' && typeof value === 'string' && !isNaN(Number(value))
        ? Number(value)
        : value;

    stateBond.additional_questions.forEach((section) => {
      section.question_sets.forEach((questionSet) => {
        // Collection sets submit an array of item objects keyed by the set code;
        // convert numeric child answers within each item. Address answers are
        // objects and never coerced.
        if (isCollectionQuestionSet(questionSet)) {
          const items = converted[questionSet.question_set_code];
          if (!Array.isArray(items)) {
            return;
          }
          converted[questionSet.question_set_code] = items.map((item: any) => {
            if (!item || typeof item !== 'object') {
              return item;
            }
            const convertedItem = { ...item };
            questionSet.questions.forEach((question) => {
              if (numericQuestionTypes.includes(question.question_type) && question.question_code in convertedItem) {
                convertedItem[question.question_code] = toNumber(convertedItem[question.question_code]);
              }
            });
            return convertedItem;
          });
          return;
        }

        questionSet.questions.forEach((question) => {
          if (numericQuestionTypes.includes(question.question_type) && question.question_code in converted) {
            converted[question.question_code] = toNumber(converted[question.question_code]);
          }
        });
      });
    });

    return converted;
  }


  private serializeContactsPayload(form: FormGroup): any | null {
    const communications = form.get('communications') as FormGroup<CIPXQuoteRequestIntakeFormCommunications> | null;
    if (!communications) return null;

    const contactsArray = communications.get('contacts') as FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>> | null;
    if (!contactsArray) return null;

    const use_same_for_renewals = Boolean(communications.get('use_same_for_renewals')?.value ?? true);

    const channelValue = (email: boolean, phone: boolean, sms: boolean): number =>
      (email ? 1 : 0) | (phone ? 2 : 0) | (sms ? 4 : 0);

    const contacts = contactsArray.controls.map((ctrl) => {
      const raw = ctrl.getRawValue();
      const type: string = raw.contact_type;
      const emailOn = Boolean(raw.email_enabled);
      const phoneOn = Boolean(raw.phone_enabled);
      const smsOn = Boolean(raw.sms_enabled);
      const newVal = channelValue(emailOn, phoneOn, smsOn);

      const renewalEmailOn = use_same_for_renewals ? emailOn : Boolean(raw.renewal_email_enabled);
      const renewalPhoneOn = use_same_for_renewals ? phoneOn : Boolean(raw.renewal_phone_enabled);
      const renewalVal = channelValue(renewalEmailOn, renewalPhoneOn, false);

      if (type === 'account_user') {
        return {
          contact_type: type,
          user_id: raw.user_id,
          communication_channel_value: newVal,
          renewal_communication_channel_value: renewalVal,
        };
      }

      if (type === 'listener') {
        return {
          contact_type: type,
          user_id: raw.user_id,
          first_name: raw.first_name,
          last_name: raw.last_name,
          email: raw.email,
          phone: raw.phone,
          sms_phone: raw.sms_phone,
          renewal_communication_channel_value: channelValue(
            use_same_for_renewals ? emailOn : Boolean(raw.renewal_email_enabled),
            use_same_for_renewals ? phoneOn : Boolean(raw.renewal_phone_enabled),
            false,
          ),
        };
      }

      // user: omit channel values if nothing selected (no override = view defaults apply)
      const base = {
        contact_type: type,
        user_id: raw.user_id,
        first_name: raw.first_name,
        last_name: raw.last_name,
        email: raw.email,
        phone: raw.phone,
        sms_phone: raw.sms_phone,
      };
      return newVal > 0
        ? { ...base, communication_channel_value: newVal, renewal_communication_channel_value: renewalVal }
        : base;
    });

    return { contacts, use_same_for_renewals };
  }

}
